import DataTableColumnHeader from "@/components/Common/DataTable/DataTableColumnHeader";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SCENARIO_DECISION_LABELS, ScenarioDecision } from "@/consts/Scenario";
import { cn, formatDate } from "@/libs/utils";
import { ScenarioApproval } from "@/types/scenario-approvals";
import { ColumnDef } from "@tanstack/react-table";
import { ReactComponent as TableSvg } from "@/assets/images/table.svg";
import { APPROVAL_RECEIVE_TYPE } from "@/consts/Approval";

type Props = {
  setApprovalId: (approvalId: string) => void;
  setDialogType: (
    type: ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
  ) => void;
  setOpenAlert: (open: boolean) => void;
  onRowClick: (projectGroupId: string) => void;
  onTableClick: (
    approvalId: string,
    myApproval?: any,
    receiveType?: string
  ) => void;
};
export default function createColumns({
  setApprovalId,
  setDialogType,
  setOpenAlert,
  onRowClick,
  onTableClick,
}: Props): ColumnDef<ScenarioApproval>[] {
  const openDialog = (
    approvalId: string,
    type: ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
  ) => {
    setApprovalId(approvalId);
    setDialogType(type);
    setOpenAlert(true);
  };

  return [
    {
      accessorKey: "title",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인 시나리오" column={column} />
      ),
      cell: ({ row }) => {
        const { title } = row.original;
        return (
          <span className="text-gray-A text-sm font-normal truncate">
            {title}
          </span>
        );
      },
      enableSorting: false,
      size: 180,
    },
    {
      id: "scenario_detail",
      header: ({ column }) => (
        <DataTableColumnHeader title="시나리오 상세 보기" column={column} />
      ),
      cell: ({ row }) => {
        const { approval_id, my_approval } = row.original;
        const { receive_type } = my_approval || {};
        return (
          <div className="flex justify-start">
            <TableSvg
              className="w-5 h-5 p-1 shrink-0 bg-dark-red rounded-sm cursor-pointer"
              onClick={() =>
                onTableClick(approval_id, my_approval, receive_type)
              }
            />
          </div>
        );
      },
      enableSorting: false,
      size: 120,
      meta: {
        isInteractive: true,
      },
    },
    {
      accessorKey: "project_group_title",
      header: ({ column }) => (
        <DataTableColumnHeader title="소속 프로젝트" column={column} />
      ),
      cell: ({ row, getValue }) => {
        const { project_group_id, project_group_title } = row.original;

        return (
          <button
            onClick={() => onRowClick(project_group_id)}
            className="text-gray-A text-sm font-normal text-left"
          >
            {project_group_title}
          </button>
        );
      },
      enableSorting: false,
      size: 380,
      meta: {
        isInteractive: true,
      },
    },
    {
      accessorKey: "requested_by",
      header: ({ column }) => (
        <DataTableColumnHeader title="요청자" column={column} />
      ),
      cell: ({ row }) => {
        const { user_name, img_url, user_id } = row.original.requested_by;
        return (
          <div
            className="flex items-center gap-2"
            key={`${row.original.approval_id}-${user_id}`}
          >
            <Avatar className="size-4">
              <AvatarImage src={img_url} />
              <AvatarFallback>{user_name}</AvatarFallback>
            </Avatar>
            <span className="text-gray-A text-xs font-normal">{user_name}</span>
          </div>
        );
      },
      size: 230,
      enableSorting: false,
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인/반려" column={column} />
      ),
      cell: ({ row }) => {
        const { approval_id, current_stage, final_decision, my_approval } =
          row.original;
        const {
          stage = 0,
          receive_type = APPROVAL_RECEIVE_TYPE.CC,
          decision,
        } = my_approval || {};

        const isUndecided = decision === ScenarioDecision.UNDECIDED;
        const isFinished = final_decision !== ScenarioDecision.UNDECIDED;
        const beforeMyTurn = current_stage < stage;

        if (isFinished || receive_type === APPROVAL_RECEIVE_TYPE.CC) {
          return null;
        }

        return (
          <div className="flex items-center justify-start gap-3">
            <button
              className={cn(
                "py-1 px-2 flex text-white rounded-sm",
                decision === ScenarioDecision.APPROVED
                  ? "bg-gray-D text-gray-B"
                  : "bg-pink",
                beforeMyTurn && "cursor-not-allowed opacity-30"
              )}
              onClick={() => openDialog(approval_id, ScenarioDecision.REJECTED)}
              disabled={!isUndecided || beforeMyTurn || isFinished}
            >
              반려
            </button>
            <button
              className={cn(
                "py-1 px-2 flex text-white rounded-sm",
                decision === ScenarioDecision.REJECTED
                  ? "bg-gray-D text-gray-B"
                  : "bg-green",
                beforeMyTurn && "cursor-not-allowed opacity-30"
              )}
              onClick={() => openDialog(approval_id, ScenarioDecision.APPROVED)}
              disabled={!isUndecided || beforeMyTurn || isFinished}
            >
              승인
            </button>
          </div>
        );
      },
      size: 150,
      enableSorting: false,
      meta: {
        isInteractive: true,
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인 요청 시점" column={column} />
      ),
      cell: ({ row }) => (
        <span className="text-gray-A text-sm font-normal">
          {formatDate(row.original.created_at, "yyyy-MM-dd")}
        </span>
      ),
      size: 230,
      enableSorting: false,
    },
    {
      accessorKey: "current_stage",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인 상태" column={column} />
      ),
      cell: ({ row }) => {
        const { current_stage, final_decision } = row.original;

        if (final_decision === ScenarioDecision.APPROVED) {
          return (
            <span className="text-green text-[10px] font-normal">
              {SCENARIO_DECISION_LABELS[final_decision]}됨
            </span>
          );
        } else if (final_decision === ScenarioDecision.REJECTED) {
          return (
            <span className="text-pink text-[10px] font-normal">
              {SCENARIO_DECISION_LABELS[final_decision]}됨
            </span>
          );
        } else {
          return (
            <span className="text-[13px] text-gray-A font-normal">
              {current_stage}단계
            </span>
          );
        }
      },
      size: 230,
    },
  ];
}
